import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { SoundControls } from "@/components/SoundControls";

const BuildSim = lazy(() => import("@/components/games/BuildSim").then((m) => ({ default: m.BuildSim })));

const TITLE = "Palworld Building Game — Free Base Simulator";
const DESCRIPTION =
  "Free Palworld base building simulator with Cattiva: haul wood, stone, ore and fiber, raise each blueprint block by block and chase flawless build bonuses.";
const OG_IMAGE = "https://palstars.com/og/build-sim.jpg";

const STEPS = [
  { name: "Pick Normal or Hard", text: "Normal builds five blocks per blueprint from two materials; Hard builds seven blocks from three materials and a wrong material costs a second." },
  { name: "Read the blueprint", text: "Each blueprint shows the material needed for every block in order, from the Palbox platform to the breeding farm." },
  { name: "Haul the material", text: "Tap the matching wood, stone, ore or fiber — Cattiva hops with the load and the block pops into place with a sound cue." },
  { name: "Finish flawlessly", text: "Complete a blueprint without a single wasted material for a flawless bonus and a clean-build streak." },
  { name: "Share the build", text: "At the end of the shift copy your structure count and points with a share link back to the game." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld building game on Pal Stars?", a: "Cattiva's Base Builder is a free animated Palworld base building simulator: every blueprint lists the material for each block in order, and you tap wood, stone, ore or fiber to raise the structure before the timer ends." },
  { q: "Who is the builder in the simulator?", a: "Cattiva, the Neutral-element starter Pal, is your foreman — she idles with a bob animation and hops each time she hauls a material into place." },
  { q: "Is the Palworld base building game free?", a: "Yes. It runs in the browser with no download and no account, and it is cosmetic: it never pays booster packs, Palsino spins or gold." },
  { q: "What is the difference between Normal and Hard building?", a: "Normal gives 60 seconds, five blocks per blueprint and two materials. Hard gives 55 seconds, seven blocks, three materials, and every wrong material also costs a second off the clock." },
  { q: "What structures can I build?", a: "Blueprints rotate through Palworld base staples: Palbox platform, wooden foundation, stone wall segment, ranch fence, berry plantation, ore mining pit, breeding farm and power generator." },
  { q: "How many builds can I play per day?", a: "Twenty-five builds per browser each UTC day, which keeps score and share-link spam down." },
  { q: "Does the building simulator work on mobile?", a: "Yes. The material buttons are large tap targets, the build site stacks into a single column on phones, and the sitewide volume control applies." },
];

export const Route = createFileRoute("/build-sim")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Base Building Simulator — build with Cattiva" },
      { property: "og:description", content: "Haul wood, stone and ore and raise every blueprint block by block. Free animated Palworld building game." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/build-sim" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Pal Stars Arcade — free Palworld browser games" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/build-sim" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Base Building Simulator", "/build-sim"]]),
      toolSchema({
        name: "Palworld Base Building Simulator",
        description: DESCRIPTION,
        path: "/build-sim",
      }),
      howToSchema({
        name: "How to play the Palworld base building simulator",
        description: "Pick a difficulty, read the blueprint, haul the matching material for every block and finish flawlessly.",
        path: "/build-sim",
        steps: STEPS,
      }),
      faqSchema(FAQ, "https://palstars.com/build-sim"),
    ],
  }),
  component: BuildSimPage,
});

function BuildSimPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld base building simulator with Cattiva</h1>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          Cattiva's Base Builder is a free, animated Palworld building game: read the blueprint, tap the matching wood,
          stone, ore or fiber for each block and finish structures before the clock ends. It plays in one screen on desktop
          and mobile, needs no download or account, and is purely cosmetic — no packs, spins or gold.
        </p>
      </header>

      <SoundControls />

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld base building simulator</h2><p className="mt-2 text-sm text-muted-foreground">Rolling out the blueprint…</p></section>}>
        <BuildSim />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Build the real base next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/technology-tree" className="font-semibold text-primary hover:underline">Palworld technology tree →</Link> the real unlock order for every structure.</li>
          <li><Link to="/team-builder" className="font-semibold text-primary hover:underline">Palworld team builder →</Link> pick Handiwork and Transporting Pals for your base.</li>
          <li><Link to="/cake-bake" className="font-semibold text-primary hover:underline">Cake baking simulator →</Link> bake cakes with Sekhmet.</li>
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link> the full arcade.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld building game FAQ" />
    </div>
  );
}
